import { ref, computed } from 'vue';

// ===== 전역 상태 =====
// 게임 리셋 카운터 (motion 컴포넌트 강제 리렌더링용)
const gameResetCounter = ref(0);

// 애니메이션 완료 상태 (전역 관리 - 컴포넌트 재마운트 시에도 유지)
const animationStates = ref({
  upAnimationComplete: false,
  downAnimationComplete: false,
  upCountAnimationComplete: false,
  downCountAnimationComplete: false,
  upResultExpanded: false,
  downResultExpanded: false,
  upWidthAnimationComplete: false,
  downWidthAnimationComplete: false,
  bothCountAnimationsComplete: false,
});

// 전역 배팅 통계 상태
const bettingStats = ref({
  // UP 관련 통계
  upTotalBetting: 0, // 내가 UP에 배팅한 총 금액
  upExpectedProfit: 0, // UP 예상 수익
  upWin: 0, // UP에 배팅한 모든 금액 (실제 배팅만)
  upPeopleCount: 0, // UP 배팅 참여자 수
  upParticipants: [], // UP 참여자 목록 (빈 배열로 시작)
  upShowResult: false, // 게임 종료 시 결과 영역 표시 여부

  // DOWN 관련 통계
  downTotalBetting: 0, // 내가 DOWN에 배팅한 총 금액
  downExpectedProfit: 0, // DOWN 예상 수익
  downLose: 0, // DOWN에 배팅한 모든 금액 (실제 배팅만)
  downPeopleCount: 0, // DOWN 배팅 참여자 수
  downParticipants: [], // DOWN 참여자 목록 (빈 배열로 시작)
  downShowResult: false, // 게임 종료 시 결과 영역 표시 여부

  // 결과 정보 (승자: 'up' | 'down' | null)
  winner: null,
});

// ===== 계산된 값 =====
// UP 승리 금액 계산 (동적, 참여자 금액 변경 시 자동 업데이트)
const upWinAmount = computed(() => {
  const total = bettingStats.value.upParticipants.reduce((sum, p) => sum + p.amount, 0);
  return Math.floor(total * 1.2); // 테스트용: 기존 값 * 1.2
});

// DOWN 승리 금액 계산 (동적, 참여자 금액 변경 시 자동 업데이트)
const downWinAmount = computed(() => {
  const total = bettingStats.value.downParticipants.reduce((sum, p) => sum + p.amount, 0);
  return Math.floor(total * 1.2); // 테스트용: 기존 값 * 1.2
});

// ===== 배팅 관리 =====
// 내 베팅 추가/업데이트
const addMyBetting = (direction, amount, userInfo = null) => {
  // TotalBetting 값을 가져옴
  const totalBetting = direction === 'up'
    ? bettingStats.value.upTotalBetting
    : bettingStats.value.downTotalBetting;

  // 사용자 정보 기본값
  const userName = userInfo?.nickname || userInfo?.name || 'You';

  if (direction === 'up') {
    // 기존 내 베팅 찾기
    const existingMyBetting = bettingStats.value.upParticipants.find(p => p.isMy);

    if (existingMyBetting) {
      // 이미 베팅이 있으면 amount만 업데이트
      existingMyBetting.amount = totalBetting;
      existingMyBetting.name = userName;
    } else {
      // 없으면 새로 추가
      const myBetting = {
        id: `my-${direction}-${Date.now()}`,
        name: userName,
        amount: totalBetting,
        isMy: true,
      };
      bettingStats.value.upParticipants.unshift(myBetting);
    }
  } else {
    // 기존 내 베팅 찾기
    const existingMyBetting = bettingStats.value.downParticipants.find(p => p.isMy);

    if (existingMyBetting) {
      // 이미 베팅이 있으면 amount만 업데이트
      existingMyBetting.amount = totalBetting;
      existingMyBetting.name = userName;
    } else {
      // 없으면 새로 추가
      const myBetting = {
        id: `my-${direction}-${Date.now()}`,
        name: userName,
        amount: totalBetting,
        isMy: true,
      };
      bettingStats.value.downParticipants.unshift(myBetting);
    }
  }
};

// ===== 게임 결과 관리 =====
// 승자 설정 (외부에서 'up' 또는 'down'을 넘겨서 제어)
const setWinner = (direction) => {
  if (direction === 'up' || direction === 'down' || direction === null) {
    bettingStats.value.winner = direction;
  }
};

// 패자 설정 (direction을 넘기면 반대편을 승자로 설정)
const setLoser = (direction) => {
  if (direction === 'up') {
    bettingStats.value.winner = 'down';
  } else if (direction === 'down') {
    bettingStats.value.winner = 'up';
  } else if (direction === null) {
    bettingStats.value.winner = null;
  }
};

// ===== 게임 데이터 초기화 =====
// 게임 데이터 초기화 함수 (새 게임 시작 시 호출)
const resetGameData = () => {
  // 애니메이션 상태 초기화
  animationStates.value = {
    upAnimationComplete: false,
    downAnimationComplete: false,
    upCountAnimationComplete: false,
    downCountAnimationComplete: false,
    upResultExpanded: false,
    downResultExpanded: false,
    upWidthAnimationComplete: false,
    downWidthAnimationComplete: false,
    bothCountAnimationsComplete: false,
  };

  // 결과 화면 즉시 숨김 (애니메이션 없이)
  bettingStats.value.upShowResult = false;
  bettingStats.value.downShowResult = false;

  // 내 배팅 데이터 초기화
  bettingStats.value.upTotalBetting = 0;
  bettingStats.value.downTotalBetting = 0;
  bettingStats.value.upPeopleCount = 0;
  bettingStats.value.downPeopleCount = 0;
  bettingStats.value.upExpectedProfit = 0;
  bettingStats.value.downExpectedProfit = 0;

  // 모든 참여자 제거 (서버에서 새 라운드 데이터 받을 때까지)
  bettingStats.value.upParticipants = [];
  bettingStats.value.downParticipants = [];

  // 배팅 금액 초기화
  bettingStats.value.upWin = 0;
  bettingStats.value.downLose = 0;

  // 승자 초기화
  bettingStats.value.winner = null;
};

// 게임 데이터 완전 초기화 함수 (모든 데이터 제거, 빈 상태로 시작)
const resetGameDataEmpty = () => {
  // 게임 리셋 카운터 증가 (motion 컴포넌트 강제 리렌더링)
  gameResetCounter.value += 1;
  
  // 애니메이션 상태 초기화
  animationStates.value = {
    upAnimationComplete: false,
    downAnimationComplete: false,
    upCountAnimationComplete: false,
    downCountAnimationComplete: false,
    upResultExpanded: false,
    downResultExpanded: false,
    upWidthAnimationComplete: false,
    downWidthAnimationComplete: false,
    bothCountAnimationsComplete: false,
  };
  
  // 결과 화면 즉시 숨김 (애니메이션 없이)
  bettingStats.value.upShowResult = false;
  bettingStats.value.downShowResult = false;
  
  // 모든 배팅 데이터 초기화
  bettingStats.value.upTotalBetting = 0;
  bettingStats.value.downTotalBetting = 0;
  bettingStats.value.upPeopleCount = 0;
  bettingStats.value.downPeopleCount = 0;
  bettingStats.value.upExpectedProfit = 0;
  bettingStats.value.downExpectedProfit = 0;
  
  // 모든 참여자 제거 (더미 + 내 베팅 모두)
  bettingStats.value.upParticipants = [];
  bettingStats.value.downParticipants = [];
  
  // 배팅 금액 초기화
  bettingStats.value.upWin = 0;
  bettingStats.value.downLose = 0;
  
  // 승자 설정 (기본적으로 UP이 승자)
  bettingStats.value.winner = 'up';
};

// ===== Export =====
export const useBettingStats = () => {
  return {
    bettingStats,
    upWinAmount,
    downWinAmount,
    addMyBetting,
    setWinner,
    setLoser,
    resetGameData,
    resetGameDataEmpty,
    gameResetCounter,
    animationStates, // 애니메이션 상태 전역 관리
  };
};

